/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file assetSlice.js
 * @description Redux slice for managing asset data loading and caching
 */

import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";

import {convertBlobToBase64} from "utils/misc";

/**
 * @function getInitialState
 * @description Creates the initial state object for the asset slice
 * @param {Object} cachedAssets - Initial cached assets object
 * @returns {Object} The initial state object
 */
export const getInitialState = (cachedAssets = {}) => ({
  cachedAssets,
  error: null,
  loading: false,
});

/**
 * @function loadAsset
 * @description Async thunk to load an asset from the API
 * @param {Object} data - Asset data request parameters
 * @param {string} data.type - Type of asset
 * @param {string} data.id - Asset identifier
 * @param {string} data.originId - Origin identifier
 * @param {string} data.lineId - Line identifier
 * @returns {Promise<Object>} Object containing the asset and its ID
 */
export const loadAsset = createAsyncThunk("assets/loadAsset", async (data, thunkAPI) => {
  try {
    const axios = createAxiosInstance(thunkAPI.dispatch);
    axios.defaults.headers["Content-Type"] = null;
    axios.defaults.headers["Accept"] = "*/*";

    const result = await axios.get(
      `/player/cms/assets/${data.type}/${data.id}?originId=${data.originId}&lineId=${data.lineId}`,
      {
        responseType: "blob",
      },
    );
    const base64Data = await convertBlobToBase64(result.data);

    return { asset: base64Data, assetId: data.id };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain asset", // serializable (err.response.data)
      name: "Asset Fetch Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant assetSlice
 * @description Redux slice for asset management
 */
const assetSlice = createSlice({
  extraReducers: (builder) =>
    builder
      .addCase(loadAsset.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(loadAsset.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(loadAsset.fulfilled, (state, action) => {
        state.cachedAssets[action.payload.assetId] = action.payload.asset;
        state.error = null;
        state.loading = false;
      }),
  initialState: getInitialState(),
  name: "assets",
  reducers: {},
});

const { reducer } = assetSlice;
export default reducer;
